/**
 * Collection page
 *
 * Handles:
 * - Filter drawer open / close with focus trap & a11y
 * - AJAX filtering with dynamic "Show x results" button
 * - Sort select
 * - Infinite scroll (auto), load-more button, or classic pagination
 * - URL updates (?page=x, filter params, sort_by)
 */

(function() {
    'use strict';

    var getSection = function() {
        return document.querySelector('.section-collection');
    };
    var getHeaderSection = function() {
        return document.querySelector('.section-collection-header');
    };
    var section = getSection();
    var headerSection = getHeaderSection();
    if (!section) return;

    var SECTION_ID = section.dataset.sectionId;
    var HEADER_SECTION_ID = headerSection ? headerSection.dataset.sectionId : null;
    var LOAD_MODE = section.dataset.loadMore || 'auto_scroll';

    var isLoading = false;
    var _prevFocused = null;

    function findDirectChildById(id) {
        if (!id) return null;
        var children = document.body.children;
        for (var i = 0; i < children.length; i++) {
            if (children[i].id === id) {
                return children[i];
            }
        }
        return null;
    }

    function moveDrawersToBody() {
        var drawersContainer = getHeaderSection() || getSection();
        if (drawersContainer) {
            var filterDrawer = drawersContainer.querySelector('.collection-filter-drawer');
            if (filterDrawer) {
                var existingFilter = findDirectChildById(filterDrawer.id);
                if (existingFilter && existingFilter !== filterDrawer) {
                    existingFilter.remove();
                }
                document.body.appendChild(filterDrawer);
            }
            var viewDrawer = drawersContainer.querySelector('.collection-view-drawer');
            if (viewDrawer) {
                var existingView = findDirectChildById(viewDrawer.id);
                if (existingView && existingView !== viewDrawer) {
                    existingView.remove();
                }
                document.body.appendChild(viewDrawer);
            }
        }
    }

    moveDrawersToBody();

    function applyDisplayContents() {
        var liveHeader = getHeaderSection();
        if (liveHeader) {
            liveHeader.style.display = 'contents';
            var wrapper = liveHeader.closest('.shopify-section');
            if (wrapper) {
                wrapper.style.display = 'contents';
            }
        }
    }

    /* ── helpers ───────────────────────────────────────────────── */

    function updateURL(url, replace) {
        if (replace) {
            window.history.replaceState({}, '', url);
        } else {
            window.history.pushState({}, '', url);
        }
    }

    /**
     * Fetch a section using the Sections Rendering API (?sections=).
     * This preserves template-assigned section settings unlike ?section_id=.
     * Returns a parsed Document from the section HTML.
     */
    function fetchSectionDoc(url) {
        var separator = url.indexOf('?') > -1 ? '&' : '?';
        var sectionIds = [SECTION_ID];
        if (HEADER_SECTION_ID) {
            sectionIds.push(HEADER_SECTION_ID);
        }
        var fetchUrl = url + separator + 'sections=' + sectionIds.join(',');

        return fetch(fetchUrl)
            .then(function(res) {
                return res.json();
            });
    }

    /* ── Section replacement (for filter / sort / popstate) ───── */

    function replaceSection(json, keepDrawerOpen) {
        var liveSection = getSection();
        var currentView = liveSection ? (liveSection.getAttribute('data-view') || 'model') : 'model';

        var openGroupIndexes = [];
        var openGroupScrollTop = 0;
        if (keepDrawerOpen) {
            var currentDrawer = getFilterDrawer();
            if (currentDrawer) {
                var groups = currentDrawer.querySelectorAll('.collection-filter-group');
                groups.forEach(function(group, idx) {
                    if (group.classList.contains('is-open')) {
                        openGroupIndexes.push(idx);
                    }
                });
                var scrollEl = currentDrawer.querySelector('.collection-filter-drawer__body-scroll');
                if (scrollEl) {
                    openGroupScrollTop = scrollEl.scrollTop;
                }
            }
        }

        // 1. Replace the main collection grid section
        var mainHtml = json[SECTION_ID];
        if (mainHtml) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(mainHtml, 'text/html');
            var newSection = doc.querySelector('.section-collection');
            if (keepDrawerOpen && newSection) {
                var newDrawer = newSection.querySelector('.collection-filter-drawer');
                if (newDrawer) {
                    newDrawer.classList.add('no-transition');
                }
            }
            if (newSection && liveSection && liveSection.parentNode) {
                liveSection.parentNode.replaceChild(newSection, liveSection);
                section = newSection;
                section.setAttribute('data-view', currentView);
            }
        }

        // 2. Replace the header section if present
        if (HEADER_SECTION_ID) {
            var headerHtml = json[HEADER_SECTION_ID];
            if (headerHtml) {
                var parser = new DOMParser();
                var doc = parser.parseFromString(headerHtml, 'text/html');
                var newHeader = doc.querySelector('.section-collection-header');
                if (keepDrawerOpen && newHeader) {
                    var newDrawer = newHeader.querySelector('.collection-filter-drawer');
                    if (newDrawer) {
                        newDrawer.classList.add('no-transition');
                    }
                }
                var liveHeader = getHeaderSection();
                if (newHeader && liveHeader && liveHeader.parentNode) {
                    liveHeader.parentNode.replaceChild(newHeader, liveHeader);
                    headerSection = newHeader;
                }
            }
        }

        applyDisplayContents();
        moveDrawersToBody();

        if (LOAD_MODE === 'auto_scroll') {
            initAutoScroll();
        }

        if (typeof quickAddButton === 'function') {
            quickAddButton();
        }

        initAccordion();
        initPriceSlider();
        initStickyToolbar();
        initActiveFiltersSlider();

        /* If drawer was open (e.g. sort changed inside drawer), re-open it on the new section */
        if (keepDrawerOpen) {
            var drawer = getFilterDrawer();
            if (drawer) {
                drawer.setAttribute('aria-hidden', 'false');
                document.body.classList.add('open-filter');

                var newGroups = drawer.querySelectorAll('.collection-filter-group');
                newGroups.forEach(function(group, idx) {
                    var summary = group.querySelector('.collection-filter-group__title');
                    var body = group.querySelector('.collection-filter-group__body');
                    if (openGroupIndexes.includes(idx)) {
                        group.classList.add('is-open');
                        if (summary) {
                            summary.classList.add('is-open');
                            summary.setAttribute('aria-expanded', 'true');
                        }
                        if (body) {
                            body.classList.add('is-open');
                            body.style.maxHeight = 'none';
                        }
                    } else {
                        group.classList.remove('is-open');
                        if (summary) {
                            summary.classList.remove('is-open');
                            summary.setAttribute('aria-expanded', 'false');
                        }
                        if (body) {
                            body.classList.remove('is-open');
                            body.style.maxHeight = '0px';
                        }
                    }
                });

                var scrollEl = drawer.querySelector('.collection-filter-drawer__body-scroll');
                if (scrollEl) {
                    scrollEl.scrollTop = openGroupScrollTop;
                }

                /* Force layout reflow so changes apply instantly without transition */
                drawer.offsetHeight;

                /* Remove no-transition class in the next frame so transitions work for future actions */
                requestAnimationFrame(function() {
                    drawer.classList.remove('no-transition');
                });
            }
        }
    }

    /* ── Filter drawer ─────────────────────────────────────────── */

    function getFilterDrawer() {
        var id = HEADER_SECTION_ID || SECTION_ID;
        return document.getElementById('collection-filter-drawer-' + id);
    }

    function openFilterDrawer() {
        var drawer = getFilterDrawer();
        if (!drawer) return;
        _prevFocused = document.activeElement;
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('open-filter');
        drawer.focus();
    }

    function closeFilterDrawer() {
        var drawer = getFilterDrawer();
        document.body.classList.remove('open-filter');
        if (drawer) {
            drawer.setAttribute('aria-hidden', 'true');
        }
        if (_prevFocused && _prevFocused.focus) _prevFocused.focus();
    }

    /* ── View Preference drawer ────────────────────────────────── */

    function getViewPreferenceDrawer() {
        var id = HEADER_SECTION_ID || SECTION_ID;
        return document.getElementById('collection-view-drawer-' + id);
    }

    function openViewPreferenceDrawer() {
        var drawer = getViewPreferenceDrawer();
        if (!drawer) return;

        /* Synchronize drawer inputs with currently applied states */
        var currentView = section.getAttribute('data-view') || 'model';
        var activeRadio = drawer.querySelector('input[name="view_mode"][value="' + currentView + '"]');
        if (activeRadio) {
            activeRadio.checked = true;
        }

        var activeDesktopLayoutBtn = document.querySelector('.collection-layouts .collection-layout-btn.is-active');
        if (activeDesktopLayoutBtn) {
            var currentLayout = activeDesktopLayoutBtn.dataset.layout;
            var drawerLayoutBtns = drawer.querySelectorAll('.collection-layout-btn');
            drawerLayoutBtns.forEach(function(btn) {
                btn.classList.toggle('is-active', btn.dataset.layout === currentLayout);
            });
        }

        _prevFocused = document.activeElement;
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('open-view-drawer');
        drawer.focus();
    }

    function closeViewPreferenceDrawer() {
        var drawer = getViewPreferenceDrawer();
        document.body.classList.remove('open-view-drawer');
        if (drawer) {
            drawer.setAttribute('aria-hidden', 'true');
        }
        if (_prevFocused && _prevFocused.focus) _prevFocused.focus();
    }

    function closeAllDrawers() {
        closeFilterDrawer();
        closeViewPreferenceDrawer();
    }

    window.collectionDrawers = {
        closeFilterDrawer: closeFilterDrawer,
        closeViewPreferenceDrawer: closeViewPreferenceDrawer,
        closeAllDrawers: closeAllDrawers
    };

    /* ── Availability injection ───────────────────────────────── */

    /**
     * When a size filter is active, automatically add filter.v.availability=1
     * so only in-stock sizes are shown.
     */
    function injectAvailabilityForSize(params) {
        var hasSize = false;
        params.forEach(function(value, key) {
            if (key.toLowerCase().indexOf('size') > -1) {
                hasSize = true;
            }
        });
        if (hasSize) {
            params.set('filter.v.availability', '1');
        }
    }

    /* ── Dynamic "Show x results" on filter changes ────────────── */

    function updateResultCount() {
        var drawer = getFilterDrawer();
        if (!drawer) return;

        var form = drawer.querySelector('.collection-filter-form');
        if (!form) return;

        /* FIX 2: Update per-filter count badges instantly from DOM — no fetch needed */
        var filterGroups = form.querySelectorAll('.collection-filter-group');
        filterGroups.forEach(function(group) {
            var checkedBoxes = group.querySelectorAll('input[type="checkbox"]:checked');
            var countEl = group.querySelector('.collection-filter-count');
            var summary = group.querySelector('.collection-filter-group__title');
            if (!summary) return;

            if (checkedBoxes.length > 0) {
                if (countEl) {
                    /* Update existing count badge */
                    countEl.textContent = '(' + checkedBoxes.length + ')';
                } else {
                    /* Create badge — insert inside h6 if present, otherwise before SVG arrow */
                    var span = document.createElement('span');
                    span.className = 'collection-filter-count';
                    span.textContent = '(' + checkedBoxes.length + ')';
                    var h6 = summary.querySelector('h6');
                    if (h6) {
                        /* Append inside h6 so it stays inline with the filter label */
                        h6.appendChild(span);
                    } else {
                        /* Fallback: insert before the arrow SVG */
                        var icon = summary.querySelector('svg');
                        if (icon) {
                            summary.insertBefore(span, icon);
                        } else {
                            summary.appendChild(span);
                        }
                    }
                }
            } else {
                /* Remove badge when nothing is checked */
                if (countEl) countEl.remove();
            }
        });

        /* Fetch updated product count for the Apply button */
        var formData = new FormData(form);
        var params = new URLSearchParams();

        /* Skip default price range values — same logic as handleFilterSubmit */
        var priceMinInput = form.querySelector('.collection-filter-price__inputs input:first-child');
        var priceMaxInput = form.querySelector('.collection-filter-price__inputs input:last-child');
        var priceRangeMax = form.querySelector('.range-max');
        var defaultPriceMax = priceRangeMax ? priceRangeMax.max : null;

        for (var pair of formData.entries()) {
            var key = pair[0];
            var val = pair[1];
            if (!val) continue;
            if (priceMinInput && key === priceMinInput.name) {
                if (val === '0' || parseFloat(val) === 0) continue;
            }
            if (priceMaxInput && key === priceMaxInput.name) {
                if (defaultPriceMax && parseFloat(val) >= parseFloat(defaultPriceMax)) continue;
            }
            params.append(key, val);
        }

        var sortRadio = form.querySelector('.collection-sort-radio:checked');
        if (sortRadio) {
            params.set('sort_by', sortRadio.value);
        }

        injectAvailabilityForSize(params);

        var fetchUrl = form.dataset.collectionUrl + '?' + params.toString();

        fetchSectionDoc(fetchUrl)
            .then(function(json) {
                var html = json[SECTION_ID];
                if (!html) return;
                var parser = new DOMParser();
                var doc = parser.parseFromString(html, 'text/html');
                var newSection = doc.querySelector('.section-collection');
                if (!newSection) return;

                var count = newSection.dataset.productsCount || '0';
                var applyBtn = drawer.querySelector('.collection-filter-apply');
                if (applyBtn) {
                    applyBtn.textContent = applyBtn.textContent.replace(/\d+/, count);
                }
            })
            .catch(console.error);
    }

    /* ── Filter form submit ────────────────────────────────────── */

    function handleFilterSubmit(e, keepDrawerOpen) {
        var form;
        if (e && e.preventDefault) {
            e.preventDefault();
            var target = e.target;
            form = (target && target.tagName === 'FORM') ? target : (target ? target.closest('form') : null);
        } else if (e instanceof HTMLFormElement) {
            form = e;
        } else if (e && e.closest) {
            form = e.closest('form');
        }
        if (!form) return;
        var formData = new FormData(form);
        var params = new URLSearchParams();

        /* Collect price range min/max defaults so we can skip unchanged price inputs */
        var priceMinInput = form.querySelector('.collection-filter-price__inputs input:first-child');
        var priceMaxInput = form.querySelector('.collection-filter-price__inputs input:last-child');
        var priceRangeMin = form.querySelector('.range-min');
        var priceRangeMax = form.querySelector('.range-max');

        var defaultPriceMin = priceRangeMin ? '0' : null;
        var defaultPriceMax = priceRangeMax ? priceRangeMax.max : null;

        for (var pair of formData.entries()) {
            var key = pair[0];
            var val = pair[1];

            /* Skip empty values */
            if (!val) continue;

            /* Skip price min if it equals the default (0) */
            if (priceMinInput && key === priceMinInput.name) {
                if (val === defaultPriceMin || val === '' || parseFloat(val) === 0) continue;
            }

            /* Skip price max if it equals the full range max (unchanged) */
            if (priceMaxInput && key === priceMaxInput.name) {
                if (defaultPriceMax && val === defaultPriceMax) continue;
                if (defaultPriceMax && parseFloat(val) >= parseFloat(defaultPriceMax)) continue;
            }

            params.append(key, val);
        }

        var sortRadio = form.querySelector('.collection-sort-radio:checked');
        if (sortRadio) {
            params.set('sort_by', sortRadio.value);
        }

        injectAvailabilityForSize(params);

        var newUrl = form.dataset.collectionUrl + '?' + params.toString();

        updateURL(newUrl);

        fetchSectionDoc(newUrl)
            .then(function(doc) {
                replaceSection(doc, keepDrawerOpen);
                if (!keepDrawerOpen) {
                    closeFilterDrawer();
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }
            })
            .catch(console.error);
    }

    /* ── Sort ──────────────────────────────────────────────────── */

    function handleSort(e) {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        url.searchParams.delete('page');

        updateURL(url.toString());

        /* Keep drawer open if it was open when sort changed */
        var drawerWasOpen = document.body.classList.contains('open-filter');

        fetchSectionDoc(url.toString())
            .then(function(doc) {
                replaceSection(doc, drawerWasOpen);
            })
            .catch(console.error);
    }

    /* ── Load more / infinite scroll ───────────────────────────── */

    function loadNextPage(loadMoreEl) {
        if (isLoading || !loadMoreEl) return;

        var nextUrl = loadMoreEl.getAttribute('href');
        if (!nextUrl) return;

        isLoading = true;
        loadMoreEl.classList.add('is-loading');

        fetchSectionDoc(nextUrl)
            .then(function(json) {
                var mainHtml = json[SECTION_ID];
                if (!mainHtml) return;
                var parser = new DOMParser();
                var doc = parser.parseFromString(mainHtml, 'text/html');
                var productGrid = section.querySelector('.collection-grid');
                var newGrid = doc.querySelector('.collection-grid');

                /* Append new products to the live grid */
                if (newGrid && productGrid) {
                    var items = newGrid.querySelectorAll('.product-item');
                    for (var i = 0; i < items.length; i++) {
                        productGrid.appendChild(items[i]);
                    }
                }

                /* Check if there is another page after the one we just loaded */
                var nextLink = doc.querySelector('[data-load-more]');
                var hasMore = false;

                if (nextLink && nextLink.getAttribute('href')) {
                    loadMoreEl.setAttribute('href', nextLink.getAttribute('href'));
                    hasMore = true;
                } else {
                    /* Fallback: use section data attributes to determine next page */
                    var newSection = doc.querySelector('.section-collection');
                    if (newSection) {
                        var totalPages = parseInt(newSection.dataset.totalPages) || 0;
                        var currentPage = parseInt(newSection.dataset.currentPage) || 0;
                        if (currentPage < totalPages) {
                            var nextPageUrl = new URL(nextUrl, window.location.origin);
                            nextPageUrl.searchParams.set('page', currentPage + 1);
                            loadMoreEl.setAttribute('href', nextPageUrl.pathname + nextPageUrl.search);
                            hasMore = true;
                        }
                    }
                }

                if (hasMore) {
                    loadMoreEl.style.display = '';
                } else {
                    /* No more pages — hide the trigger */
                    loadMoreEl.style.display = 'none';
                    if (autoScrollObserver) autoScrollObserver.disconnect();
                }

                /* Update "Page X of Y" text (load_more_btn mode) */
                var liveInfo = section.querySelector('.collection-pagination-info');
                var newInfo = doc.querySelector('.collection-pagination-info');
                if (liveInfo && newInfo) {
                    liveInfo.innerHTML = newInfo.innerHTML;
                } else if (liveInfo && !hasMore) {
                    var totalPages = section.dataset.totalPages || '?';
                    liveInfo.textContent = liveInfo.textContent.replace(/\d+(\s*(?:of|\/)\s*)\d+/, totalPages + '$1' + totalPages);
                }

                /* Update dataset with loaded page */
                try {
                    var urlObj = new URL(nextUrl, window.location.origin);
                    var page = urlObj.searchParams.get('page');
                    if (page) {
                        section.dataset.currentPage = page;
                    }
                } catch (e) {
                    /* ignore URL parse errors */ }

                /* Re-bind quick add */
                if (typeof quickAddButton === 'function') {
                    quickAddButton();
                }
            })
            .catch(function(err) {
                console.error('Load more error:', err);
            })
            .finally(function() {
                isLoading = false;
                loadMoreEl.classList.remove('is-loading');
            });
    }

    /* ── Accordion ─────────────────────────────────────────────── */

    function initAccordion() {
        var drawer = getFilterDrawer();
        if (!drawer) return;

        var groups = drawer.querySelectorAll('.collection-filter-group');

        groups.forEach(function(el) {
            var summary = el.querySelector('.collection-filter-group__title');
            var body = el.querySelector('.collection-filter-group__body');
            if (!summary || !body) return;
            if (summary.hasAccordionListener) return;
            summary.hasAccordionListener = true;

            /* Set selected class on already-checked items (e.g. active filters on page load) */
            el.querySelectorAll('.collection-filter-item input[type="checkbox"]:checked').forEach(function(cb) {
                var item = cb.closest('.collection-filter-item');
                if (item) item.classList.add('selected');
            });

            /* Set initial open state */
            var isOpen = el.classList.contains('is-open');
            if (isOpen) {
                body.classList.add('is-open');
                summary.classList.add('is-open');
                summary.setAttribute('aria-expanded', 'true');
                body.style.maxHeight = body.scrollHeight + 'px';
            } else {
                body.classList.remove('is-open');
                summary.classList.remove('is-open');
                summary.setAttribute('aria-expanded', 'false');
                body.style.maxHeight = '0px';
            }

            summary.addEventListener('click', function(e) {
                e.preventDefault();

                var currentlyOpen = el.classList.contains('is-open');

                if (currentlyOpen) {
                    /* Closing */
                    body.style.maxHeight = body.scrollHeight + 'px';
                    body.offsetHeight; // Force reflow
                    body.style.maxHeight = '0px';
                    body.classList.remove('is-open');
                    summary.classList.remove('is-open');
                    el.classList.remove('is-open');
                    summary.setAttribute('aria-expanded', 'false');
                } else {
                    /* Opening */
                    /* Close other groups */
                    var allGroups = drawer.querySelectorAll('.collection-filter-group');
                    allGroups.forEach(function(otherGroup) {
                        if (otherGroup !== el && otherGroup.classList.contains('is-open')) {
                            var otherSummary = otherGroup.querySelector('.collection-filter-group__title');
                            var otherBody = otherGroup.querySelector('.collection-filter-group__body');
                            if (otherBody) {
                                otherBody.style.maxHeight = otherBody.scrollHeight + 'px';
                                otherBody.offsetHeight; // force reflow
                                otherBody.style.maxHeight = '0px';
                                otherBody.classList.remove('is-open');
                            }
                            if (otherSummary) {
                                otherSummary.classList.remove('is-open');
                                otherSummary.setAttribute('aria-expanded', 'false');
                            }
                            otherGroup.classList.remove('is-open');
                        }
                    });

                    el.classList.add('is-open');
                    body.classList.add('is-open');
                    summary.classList.add('is-open');
                    summary.setAttribute('aria-expanded', 'true');
                    body.style.maxHeight = body.scrollHeight + 'px';
                    
                    var onTransitionEnd = function(event) {
                        if (event.propertyName === 'max-height') {
                            if (el.classList.contains('is-open')) {
                                body.style.maxHeight = 'none';
                            }
                            body.removeEventListener('transitionend', onTransitionEnd);
                        }
                    };
                    body.addEventListener('transitionend', onTransitionEnd);
                }
            });

            summary.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    summary.click();
                }
            });
        });
    }

    function initPriceSlider() {
        var drawer = getFilterDrawer();
        if (!drawer) return;

        var container = drawer.querySelector('.collection-filter-price');
        if (!container) return;

        var rangeMin = container.querySelector('.range-min');
        var rangeMax = container.querySelector('.range-max');
        var inputMin = container.querySelector('.collection-filter-price__inputs input:first-child');
        var inputMax = container.querySelector('.collection-filter-price__inputs input:last-child');
        var labelMin = container.querySelector('.collection-filter-price__label.min');
        var labelMax = container.querySelector('.collection-filter-price__label.max');
        var slider = container.querySelector('.collection-filter-price__range-slider');

        if (!rangeMin || !rangeMax || !inputMin || !inputMax || !labelMin || !labelMax || !slider) return;

        var minGap = 0;
        var maxVal = parseInt(rangeMin.max);

        function formatMoney(val) {
            /* This is a simple formatter, ideally we'd use Shopify.formatMoney if available */
            /* But for now we just use the currency symbol from the existing labels if possible */
            var symbol = labelMin.textContent.match(/[^\d\s,.]+/);
            symbol = symbol ? symbol[0] : '';

            /* Format with currency symbol and handle decimals if needed */
            /* Simplifying for this implementation */
            return symbol + val.toLocaleString();
        }

        function updateSlider() {
            var minVal = parseInt(rangeMin.value);
            var maxVal = parseInt(rangeMax.value);

            if (maxVal - minVal < minGap) {
                if (this === rangeMin) {
                    rangeMin.value = maxVal - minGap;
                    minVal = maxVal - minGap;
                } else {
                    rangeMax.value = minVal + minGap;
                    maxVal = minVal + minGap;
                }
            }

            var minPercent = (minVal / rangeMin.max) * 100;
            var maxPercent = 100 - (maxVal / rangeMax.max) * 100;

            slider.style.setProperty('--range-min', minPercent + '%');
            slider.style.setProperty('--range-max', maxPercent + '%');

            labelMin.textContent = formatMoney(minVal);
            labelMax.textContent = formatMoney(maxVal);

            inputMin.value = minVal;
            inputMax.value = maxVal;
        }

        rangeMin.addEventListener('input', updateSlider);
        rangeMax.addEventListener('input', updateSlider);

        slider.addEventListener('click', function(e) {
            // Ignore click if it was directly on one of the range inputs (like dragging the thumb)
            if (e.target.tagName === 'INPUT') return;

            var rect = slider.getBoundingClientRect();
            var clickPercent = (e.clientX - rect.left) / rect.width;
            var rangeMaxVal = parseInt(rangeMin.max) || 100;
            var clickedValue = Math.round(clickPercent * rangeMaxVal);
            clickedValue = Math.max(0, Math.min(rangeMaxVal, clickedValue));

            var currentMin = parseInt(rangeMin.value);
            var currentMax = parseInt(rangeMax.value);

            var distMin = Math.abs(clickedValue - currentMin);
            var distMax = Math.abs(clickedValue - currentMax);

            var inputToUpdate;
            if (distMin < distMax) {
                rangeMin.value = clickedValue;
                inputToUpdate = rangeMin;
            } else {
                rangeMax.value = clickedValue;
                inputToUpdate = rangeMax;
            }

            updateSlider();

            // Dispatch input event to trigger count badge updates
            inputToUpdate.dispatchEvent(new Event('input', { bubbles: true }));
            // Dispatch change event to trigger AJAX collection filter updates
            inputToUpdate.dispatchEvent(new Event('change', { bubbles: true }));
        });

        /* Initial update */
        updateSlider();
    }

    var autoScrollObserver = null;

    function initAutoScroll() {
        if (autoScrollObserver) autoScrollObserver.disconnect();

        var loadMoreEl = section.querySelector('.collection-load-more--auto[data-load-more]');
        if (!loadMoreEl) return;

        autoScrollObserver = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                if (entry.isIntersecting && !isLoading) {
                    loadNextPage(loadMoreEl);
                }
            });
        }, {
            rootMargin: '200px'
        });

        autoScrollObserver.observe(loadMoreEl);
    }

    /* ── Delegated event listeners ─────────────────────────────── */

    /* Overlay click closes filter (capture phase to bypass cart-drawer stopPropagation) */
    var overlay = document.querySelector('.overlay-box');
    if (overlay) {
        overlay.addEventListener('click', function() {
            if (document.body.classList.contains('open-filter') || document.body.classList.contains('open-view-drawer')) {
                closeAllDrawers();
            }
        }, true);
    }

    document.addEventListener('click', function(e) {
        /* Click on filter item (entire LI) */
        var filterItem = e.target.closest('.collection-filter-item');
        if (filterItem) {
            var checkbox = filterItem.querySelector('input[type="checkbox"]');
            if (checkbox && !checkbox.disabled) {
                var label = filterItem.querySelector('.collection-filter-label');
                if (label && !label.contains(e.target) && e.target !== label && e.target !== checkbox) {
                    e.preventDefault();
                    checkbox.checked = !checkbox.checked;
                    var event = new Event('change', { bubbles: true });
                    checkbox.dispatchEvent(event);
                    return;
                }
            }
        }

        /* Click on sort item (entire LI) */
        var sortItem = e.target.closest('.collection-sort-item');
        if (sortItem) {
            var radio = sortItem.querySelector('input[type="radio"]');
            if (radio && !radio.disabled) {
                var label = sortItem.querySelector('.collection-sort-label');
                if (label && !label.contains(e.target) && e.target !== label && e.target !== radio) {
                    e.preventDefault();
                    radio.checked = true;
                    var event = new Event('change', { bubbles: true });
                    radio.dispatchEvent(event);
                    return;
                }
            }
        }

        /* Open filter drawer */
        if (e.target.closest('.collection-filter-toggle')) {
            openFilterDrawer();
            return;
        }

        /* Close filter drawer */
        if (e.target.closest('.collection-filter-close:not(.collection-view-close)')) {
            closeFilterDrawer();
            return;
        }

        /* Click on drawer backdrop (outside inner panel) */
        var drawer = getFilterDrawer();
        if (drawer && document.body.classList.contains('open-filter') && e.target === drawer) {
            closeFilterDrawer();
            return;
        }

        var viewDrawer = getViewPreferenceDrawer();
        if (viewDrawer && document.body.classList.contains('open-view-drawer') && e.target === viewDrawer) {
            closeViewPreferenceDrawer();
            return;
        }

        /* Load more button */
        var loadMoreBtn = e.target.closest('.collection-load-more--btn[data-load-more]');
        if (loadMoreBtn) {
            e.preventDefault();
            loadNextPage(loadMoreBtn);
            return;
        }

        /* Model/Product view toggle */
        var viewBtn = e.target.closest('.collection-model-view [data-view]');
        if (viewBtn && section) {
            var view = viewBtn.dataset.view;
            section.setAttribute('data-view', view);

            var viewBtns = (getHeaderSection() || section).querySelectorAll('.collection-model-view [data-view]');
            viewBtns.forEach(function(btn) {
                btn.classList.toggle('is-active', btn === viewBtn);
            });
            return;
        }

        /* Open View Preference drawer */
        if (e.target.closest('.collection-view-preference-toggle')) {
            openViewPreferenceDrawer();
            return;
        }

        /* Close View Preference drawer */
        if (e.target.closest('.collection-view-close')) {
            closeViewPreferenceDrawer();
            return;
        }

        /* Grid layout button (both drawer and desktop) */
        var layoutBtn = e.target.closest('.collection-layout-btn');
        if (layoutBtn && section) {
            var layout = layoutBtn.dataset.layout;

            if (layoutBtn.closest('.collection-view-drawer')) {
                /* If inside drawer, toggle active state for drawer buttons and update toolbar icon */
                var drawerLayoutBtns = layoutBtn.closest('.collection-view-drawer').querySelectorAll('.collection-layout-btn');
                drawerLayoutBtns.forEach(function(btn) {
                    btn.classList.toggle('is-active', btn.dataset.layout === layout);
                });
                updateToolbarIcon(layoutBtn);
            } else {
                /* Clicked on desktop - apply immediately */
                var allLayoutBtns = document.querySelectorAll('.collection-layout-btn');
                allLayoutBtns.forEach(function(btn) {
                    btn.classList.toggle('is-active', btn.dataset.layout === layout);
                });
                updateToolbarIcon(layoutBtn);
                updateGridColumns(layout);
            }
            return;
        }

        /* Save Preference button */
        if (e.target.closest('.collection-view-apply')) {
            var drawer = getViewPreferenceDrawer();
            if (drawer) {
                var selectedView = drawer.querySelector('input[name="view_mode"]:checked');
                if (selectedView && section) {
                    var view = selectedView.value;
                    section.setAttribute('data-view', view);

                    /* Sync desktop buttons too */
                    var viewBtns = document.querySelectorAll('.collection-model-view [data-view]');
                    viewBtns.forEach(function(btn) {
                        btn.classList.toggle('is-active', btn.dataset.view === view);
                    });
                }

                var activeLayoutBtn = drawer.querySelector('.collection-layout-btn.is-active');
                if (activeLayoutBtn) {
                    var layout = activeLayoutBtn.dataset.layout;

                    /* Sync ALL layout buttons (desktop and drawer) so they stay visually in sync */
                    var allLayoutBtns = document.querySelectorAll('.collection-layout-btn');
                    allLayoutBtns.forEach(function(btn) {
                        btn.classList.toggle('is-active', btn.dataset.layout === layout);
                    });

                    /* Update toolbar icon */
                    updateToolbarIcon(activeLayoutBtn);
                    updateGridColumns(layout);
                }
            }
            closeViewPreferenceDrawer();
            return;
        }
    });

    function checkCollectionCarouselPresence() {
        var carouselSection = document.querySelector('.section-collection-carousel');
        if (!carouselSection) {
            var layout1Btns = document.querySelectorAll('.collection-layout-btn.layout-1');
            if (layout1Btns.length > 0) {
                var wasActive = false;
                layout1Btns.forEach(function(btn) {
                    if (btn.classList.contains('is-active')) {
                        wasActive = true;
                    }
                    btn.remove();
                });

                if (wasActive) {
                    var fallbackBtn = document.querySelector('.collection-layout-btn.layout-3') || document.querySelector('.collection-layout-btn.layout-2');
                    if (fallbackBtn) {
                        var fallbackLayout = fallbackBtn.dataset.layout || '3';
                        var remainingBtns = document.querySelectorAll('.collection-layout-btn');
                        remainingBtns.forEach(function(btn) {
                            btn.classList.toggle('is-active', btn.dataset.layout === fallbackLayout);
                        });
                        updateToolbarIcon(fallbackBtn);
                        updateGridColumns(fallbackLayout);
                    }
                }
            }
        }
    }

    function restoreViewState() {
        checkCollectionCarouselPresence();
        var view = section.getAttribute('data-view');
        if (view) {
            var viewBtns = document.querySelectorAll('.collection-model-view [data-view]');
            viewBtns.forEach(function(btn) {
                btn.classList.toggle('is-active', btn.dataset.view === view);
            });
        }

        /* Restore toolbar icon and layout button states */
        var activeBtn = document.querySelector('.collection-view-drawer .collection-layout-btn.is-active') || document.querySelector('.collection-layout-btn.is-active');
        if (activeBtn) {
            updateToolbarIcon(activeBtn);
            if (activeBtn.dataset.layout) {
                updateGridColumns(activeBtn.dataset.layout);
            }
        }
        adjustPromoBanners();
    }

    function updateToolbarIcon(layoutOrBtn) {
        if (!layoutOrBtn) return;
        var layout = typeof layoutOrBtn === 'string' ? layoutOrBtn : (layoutOrBtn.dataset ? layoutOrBtn.dataset.layout : null);
        if (!layout) return;

        var drawer = getViewPreferenceDrawer();
        var mobileBtn = (drawer ? drawer.querySelector('.collection-layout-btn[data-layout="' + layout + '"]') : null) || document.querySelector('.collection-view-drawer .collection-layout-btn[data-layout="' + layout + '"]');
        var selectedIconSvg = mobileBtn ? mobileBtn.querySelector('svg') : (layoutOrBtn.querySelector ? layoutOrBtn.querySelector('svg') : null);

        var toolbarToggle = document.querySelector('.collection-view-preference-toggle');
        if (toolbarToggle && selectedIconSvg) {
            var toolbarIcon = toolbarToggle.querySelector('svg');
            if (toolbarIcon) {
                toolbarToggle.replaceChild(selectedIconSvg.cloneNode(true), toolbarIcon);
            }
        }
    }

    function updateGridColumns(layout) {
        var grid = section.querySelector('.collection-grid');
        var carouselSection = document.querySelector('.section-collection-carousel');
        if (carouselSection) {
            if (layout === '1') {
                carouselSection.style.display = '';
            } else {
                carouselSection.style.display = 'none';
            }
        }
        if (grid) {
            var columnsDesktop = 4;
            var columnsMobile = 2;
            if (layout === '1') {
                columnsDesktop = 4;
                columnsMobile = 2;
            } else if (layout === '2') {
                columnsDesktop = 3;
                columnsMobile = 1;
            } else if (layout === '3') {
                columnsDesktop = 4;
                columnsMobile = 2;
            } else if (layout === '4') {
                columnsDesktop = 6;
                columnsMobile = 3;
            }
            grid.style.setProperty('--columns-desktop', columnsDesktop);
            grid.style.setProperty('--columns-mobile', columnsMobile);

            if (layout === '1') {
                grid.classList.add('layout-1-active');
            } else {
                grid.classList.remove('layout-1-active');
            }

            if (layout === '4') {
                grid.classList.add('layout-4-active');
            } else {
                grid.classList.remove('layout-4-active');
            }

            adjustPromoBanners();
        }
    }

    function adjustPromoBanners() {
        var grid = section.querySelector('.collection-grid');
        if (!grid) return;

        var isMobile = window.innerWidth <= 991;
        var totalColumns = parseInt(grid.style.getPropertyValue(isMobile ? '--columns-mobile' : '--columns-desktop')) || (isMobile ? 2 : 4);

        var promoCards = grid.querySelectorAll('.collection-promo-card');
        promoCards.forEach(function(card) {
            var promoParent = card.closest('.collection-promo-card-parent') || card;

            var col = parseInt(card.getAttribute(isMobile ? 'data-mob-col' : 'data-desk-col')) || 1;
            var colSpan = parseInt(card.getAttribute(isMobile ? 'data-mob-col-span' : 'data-desk-col-span')) || 1;
            var row = parseInt(card.getAttribute(isMobile ? 'data-mob-row' : 'data-desk-row')) || 1;
            var rowSpan = parseInt(card.getAttribute(isMobile ? 'data-mob-row-span' : 'data-desk-row-span')) || 1;

            colSpan = Math.min(colSpan, totalColumns);
            if (col + colSpan - 1 > totalColumns) {
                col = totalColumns - colSpan + 1;
            }
            if (col < 1) col = 1;

            promoParent.style.gridColumn = col + ' / span ' + colSpan;
            promoParent.style.gridRow = row + ' / span ' + rowSpan;
        });
    }

    window.addEventListener('resize', adjustPromoBanners);

    /* Use a MutationObserver to catch any external changes to data-view or section replacement */
    var observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-view') {
                restoreViewState();
            }
            /* If section is replaced externally, re-query it */
            if (mutation.type === 'childList') {
                var newSection = getSection();
                if (newSection && newSection !== section) {
                    section = newSection;
                    restoreViewState();
                }
            }
        });
    });

    if (section && section.parentNode) {
        observer.observe(section.parentNode, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-view']
        });
    }

    /* Escape key closes filter */
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && (document.body.classList.contains('open-filter') || document.body.classList.contains('open-view-drawer'))) {
            closeAllDrawers();
            return;
        }

        /* Focus trap inside filter drawer / view drawer */
        if (e.key === 'Tab' && (document.body.classList.contains('open-filter') || document.body.classList.contains('open-view-drawer'))) {
            var drawer = document.body.classList.contains('open-filter') ? getFilterDrawer() : getViewPreferenceDrawer();
            if (!drawer) return;

            var focusable = drawer.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (!focusable.length) return;

            var first = focusable[0];
            var last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    });

    /* Sort change + filter change */
    document.addEventListener('change', function(e) {
        /* Toggle selected class on filter-item when checkbox changes */
        var checkbox = e.target.closest('.collection-filter-item input[type="checkbox"]');
        if (checkbox) {
            var item = checkbox.closest('.collection-filter-item');
            if (item) {
                item.classList.toggle('selected', checkbox.checked);
            }
        }

        if (e.target.closest('.collection-filter-form')) {
            handleFilterSubmit(e, true);
        }
    });

    /* Filter range inputs */
    document.addEventListener('input', function(e) {
        if (e.target.closest('.collection-filter-price input')) {
            clearTimeout(e.target._debounce);
            e.target._debounce = setTimeout(updateResultCount, 400);
        }
    });

    /* Filter form submit */
    document.addEventListener('submit', function(e) {
        if (e.target.closest('.collection-filter-form')) {
            handleFilterSubmit(e);
        }
    });

    /* Browser back/forward */
    window.addEventListener('popstate', function() {
        fetchSectionDoc(window.location.href)
            .then(function(json) {
                replaceSection(json);
            })
            .catch(console.error);
    });

    /* ── Sticky toolbar ────────────────────────────────────────── */

    var stickyToolbarScrollHandler = null;

    function initStickyToolbar() {
        /* Remove previous scroll listener if section was replaced */
        if (stickyToolbarScrollHandler) {
            window.removeEventListener('scroll', stickyToolbarScrollHandler);
            stickyToolbarScrollHandler = null;
        }

        var headerSec = getHeaderSection() || section;
        var wrapper = headerSec.querySelector('.collection-sticky-wrapper');
        if (!wrapper) return;

        /* Clean up any existing sentinels first to prevent duplicates */
        var oldSentinel = headerSec.querySelector('[data-sticky-sentinel]');
        if (oldSentinel) {
            oldSentinel.remove();
        }

        /* Sentinel: invisible 1px div placed just above the wrapper in normal layout flow.
           Since it is in document flow, its position accurately reflects the wrapper's natural top. */
        var sentinel = document.createElement('div');
        sentinel.setAttribute('data-sticky-sentinel', '');
        sentinel.style.cssText = 'width:1px;height:1px;margin-top:-1px;pointer-events:none;visibility:hidden;';
        sentinel.setAttribute('aria-hidden', 'true');
        wrapper.parentNode.insertBefore(sentinel, wrapper);

        var isSticky = false;
        function checkSticky() {
            var scrollY = window.pageYOffset || window.scrollY || 0;
            if (scrollY < 10) {
                if (isSticky || wrapper.classList.contains('sticky-toolbar')) {
                    isSticky = false;
                    wrapper.classList.remove('sticky-toolbar');
                }
                return;
            }
            var sentinelRect = sentinel.getBoundingClientRect();
            var headerHeight = 0;
            if (document.body.classList.contains('scroll-up')) {
                headerHeight = window.innerWidth >= 992 ? 48 : 44;
            }
            var shouldBeSticky = sentinelRect.top <= headerHeight;
            if (shouldBeSticky !== isSticky) {
                isSticky = shouldBeSticky;
                wrapper.classList.toggle('sticky-toolbar', isSticky);
            }
        }

        stickyToolbarScrollHandler = function() {
            requestAnimationFrame(checkSticky);
        };

        window.addEventListener('scroll', stickyToolbarScrollHandler);
        /* Run once initially to check correct starting state */
        checkSticky();
    }
    /* ── Active filters Splide slider ─────────────────────────── */

    var activeFiltersSplide = null;
    var _arrowClickHandler = null;

    function initActiveFiltersSlider() {
        if (activeFiltersSplide) {
            activeFiltersSplide.destroy();
            activeFiltersSplide = null;
        }

        var el = (getHeaderSection() || section).querySelector('[data-active-filters-slider]');
        if (!el) return;

        var arrowsWrapper = (getHeaderSection() || section).querySelector('.collection-active-filters__arrows');
        if (arrowsWrapper) arrowsWrapper.style.display = 'none';

        activeFiltersSplide = new Splide(el, {
            type: 'slide',
            drag: 'free',
            snap: false,
            autoWidth: true,
            pagination: false,
            arrows: false,
            gap: '0px',
            perPage: 20,
            speed: 800,
            flickPower: 400,
        });

        /* ── Arrow states based purely on track scrollLeft ── */
        function updateArrowStates() {
            var pBtn = (getHeaderSection() || section).querySelector('.collection-active-filters__arrow--prev');
            var nBtn = (getHeaderSection() || section).querySelector('.collection-active-filters__arrow--next');
            var track = el.querySelector('.splide__track');
            if (!track) return;

            var scrollLeft = Math.round(track.scrollLeft);
            var maxScroll = Math.round(track.scrollWidth - track.offsetWidth);

            if (pBtn) pBtn.disabled = (scrollLeft <= 0);
            if (nBtn) nBtn.disabled = (maxScroll <= 0) ? false : (scrollLeft >= maxScroll - 1);
        }

        /* ── Show/hide arrows wrapper based on overflow ── */
        function checkOverflow() {
            var track = el.querySelector('.splide__track');
            var list = el.querySelector('.splide__list');
            if (!track || !list) return;

            var hasOverflow = list.scrollWidth > track.offsetWidth + 2;
            if (arrowsWrapper) arrowsWrapper.style.display = hasOverflow ? 'flex' : 'none';
            updateArrowStates();
        }

        activeFiltersSplide.on('mounted', function() {
            /* Bind scroll listener on track for live arrow state updates while dragging */
            var track = el.querySelector('.splide__track');
            if (track) {
                track.addEventListener('scroll', function() {
                    clearTimeout(track._scrollTimer);
                    track._scrollTimer = setTimeout(updateArrowStates, 30);
                }, {
                    passive: true
                });
            }

            requestAnimationFrame(function() {
                requestAnimationFrame(function() {
                    checkOverflow();
                    setTimeout(checkOverflow, 150);
                });
            });
        });

        activeFiltersSplide.on('dragged', updateArrowStates);
        activeFiltersSplide.on('moved', updateArrowStates);

        activeFiltersSplide.mount();

        /* ── Resize ── */
        window.addEventListener('resize', function() {
            clearTimeout(window._arrowResizeTimer);
            window._arrowResizeTimer = setTimeout(checkOverflow, 150);
        });

        /* ── Arrow click handler ── */
        if (_arrowClickHandler) {
            document.removeEventListener('click', _arrowClickHandler);
        }

        _arrowClickHandler = function(e) {
            var isPrev = e.target.closest('.collection-active-filters__arrow--prev');
            var isNext = e.target.closest('.collection-active-filters__arrow--next');
            if (!isPrev && !isNext) return;

            var track = el.querySelector('.splide__track');
            if (!track) return;

            /* Scroll 80% of visible track width */
            var amount = Math.round(track.offsetWidth * 0.8);

            track.scrollBy({
                left: isNext ? amount : -amount,
                behavior: 'smooth',
            });

            /* Update arrow states after smooth scroll settles (~400ms) */
            setTimeout(updateArrowStates, 420);
        };

        document.addEventListener('click', _arrowClickHandler);
    }

    /* ── Init ──────────────────────────────────────────────────── */

    applyDisplayContents();
    checkCollectionCarouselPresence();

    if (LOAD_MODE === 'auto_scroll') {
        initAutoScroll();
    }

    initAccordion();
    initPriceSlider();
    initStickyToolbar();
    initActiveFiltersSlider();

})();